import * as React from 'react'

import { DataTableInfinite } from '@/components/data-table/data-table-infinite'
import { DataTableStoreProvider } from '@/lib/store/provider/DataTableStoreProvider'
import { useNuqsAdapter } from '@/lib/store/adapters/nuqs'
import { useFilterState } from '@/lib/store/hooks/useFilterState'
import { useFilterActions } from '@/lib/store/hooks/useFilterActions'
import { columnFiltersToFilterConditions } from '@/features/calls/utils/mergeFilters'
import { TabRefreshButton } from '@/components/panel/TabRefreshButton'
import { useVisitsQuery } from '@/hooks/useVisitsQuery'
import type { VisitRecord } from '@/features/visits/types'
import { ImportDataButton } from '@/features/data-import'
import { visitsColumns } from '@/features/visits/components/VisitsColumns'
import { VisitsSheetWrapper } from '@/features/visits/components/VisitsSheetDetails'
import {
  visitsFilterFields,
  visitsSchema,
} from '@/features/visits/components/visitsTableSchema'

const TABLE_ID = 'visits'

function VisitsInner() {
  const storeState = useFilterState()
  const { resetAllFilters } = useFilterActions()
  // Only real filter fields become query params — exclude store metadata (sort,
  // selected-row uuid) so opening the details sheet doesn't churn the query key.
  const apiFilters = React.useMemo(() => {
    const ids = new Set(visitsFilterFields.map((f) => String(f.value)))
    const columnFilters = Object.entries(storeState ?? {})
      .filter(([key, value]) => {
        if (!ids.has(key)) return false
        if (value === null || value === undefined) return false
        if (Array.isArray(value)) return value.length > 0
        return String(value).trim().length > 0
      })
      .map(([id, value]) => ({ id, value }))
    return columnFiltersToFilterConditions(columnFilters)
  }, [storeState])

  const query = useVisitsQuery({
    limit: 50,
    sort_by: 'visit_date',
    sort_order: 'desc',
    filters: apiFilters.length > 0 ? apiFilters : undefined,
  })

  const data = React.useMemo(
    () => query.data?.pages.flatMap((p) => p.data) ?? [],
    [query.data]
  )
  const totalRows = query.data?.pages[0]?.pagination.total ?? undefined

  const getRowId = React.useCallback((row: VisitRecord) => row.id, [])

  const SheetSlot = React.useMemo(() => <VisitsSheetWrapper />, [])

  const ToolbarActions = React.useMemo(
    () => (
      <>
        <ImportDataButton entity="visit" entityLabel="Visit" />
        <TabRefreshButton
          onRefresh={query.refetch}
          isFetching={query.isFetching}
        />
      </>
    ),
    [query.refetch, query.isFetching]
  )

  return (
    <DataTableInfinite<VisitRecord, unknown>
      tableId={TABLE_ID}
      columns={visitsColumns}
      data={data}
      filterFields={visitsFilterFields}
      manualFiltering
      isRefetching={query.isPlaceholderData}
      isError={query.isError}
      error={query.error}
      entityLabel="visits"
      onClearFilters={resetAllFilters}
      isLoading={query.isLoading}
      isFetching={query.isFetching}
      hasNextPage={query.hasNextPage}
      fetchNextPage={query.fetchNextPage}
      refetch={query.refetch}
      totalRows={totalRows}
      totalRowsFetched={data.length}
      getRowId={getRowId}
      sheetSlot={SheetSlot}
      toolbarActions={ToolbarActions}
      autoColumnWidth
    />
  )
}

export function Visits() {
  const adapter = useNuqsAdapter(visitsSchema.definition, {
    id: TABLE_ID,
    history: 'replace',
    // Namespace URL params (`${id}.`) so tables sharing the sidebar's carried
    // location.search don't bleed each other's same-named filters across tabs.
    namespaceUrlKeys: true,
  })

  return (
    <DataTableStoreProvider adapter={adapter}>
      <VisitsInner />
    </DataTableStoreProvider>
  )
}
